import { useEffect, useRef, useState } from 'react';
import { Loader2, AlertCircle, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useSumsub } from '@/hooks/useSumsub';
import { supabase } from '@/integrations/supabase/client';

interface ExtractedAddress {
  street?: string;
  city?: string;
  postalCode?: string;
  country?: string;
}

interface ExtractedDocumentDetails {
  documentType?: string;
  documentNumber?: string;
  documentExpiry?: string;
  documentIssueDate?: string;
  documentCountry?: string;
  nationality?: string;
}

interface ExtractedPOADetails {
  address?: ExtractedAddress;
  documentType?: string;
  issueDate?: string;
  issuer?: string;
}

export interface ExtractedData {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  address?: ExtractedAddress;
  document?: ExtractedDocumentDetails;
  poa?: ExtractedPOADetails;
  // Verification status
  verificationFailed?: boolean;
  failureReason?: string;
  failureCategory?: 'biometrics' | 'document' | 'aml' | 'other';
}

interface SumsubVerificationProps {
  onComplete: (extractedData?: ExtractedData) => void;
  onError?: (error: string) => void;
  email?: string;
  phone?: string;
  levelName?: string;
  includePOA?: boolean;
  /** Stable external person ID — when provided, used as the Sumsub externalUserId
   *  so the token is tied to a specific stakeholder (e.g. from the /verify link). */
  personId?: string;
  /** Full name of the person being verified — used as a fallback lookup key in the edge function */
  personName?: string;
}

/**
 * Sumsub Verification Component - Real SDK Integration
 * 
 * This component embeds the Sumsub WebSDK for identity verification.
 * After verification, it polls the session for extracted data from the webhook.
 * If verification fails, allows user to continue with appropriate messaging.
 */
export function SumsubVerification({
  onComplete,
  onError,
  email,
  phone,
  levelName,
  includePOA = true,
  personId,
  personName,
}: SumsubVerificationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [failureReason, setFailureReason] = useState<string | null>(null);
  const [failureCategory, setFailureCategory] = useState<'biometrics' | 'document' | 'aml' | 'other' | null>(null);
  const [fetchingData, setFetchingData] = useState(false);
  const [showManualContinue, setShowManualContinue] = useState(false);
  const hasLaunched = useRef(false);
  const completionHandled = useRef(false);

  const handleVerificationComplete = async (failed = false, reason?: string, category?: 'biometrics' | 'document' | 'aml' | 'other', sdkApplicantId?: string) => {
    if (completionHandled.current) return;
    completionHandled.current = true;

    
    if (failed) {
      setVerificationFailed(true);
      setFailureReason(reason || 'Verification could not be completed');
      setFailureCategory(category || 'other');
    }
    
    setFetchingData(true);
    
    // Pass the applicantId from the SDK payload directly — this is the most reliable lookup
    // Also pass the actual SDK externalUserId (not the personId) so the edge function can find the applicant
    await fetchExtractedDataFromSumsub(email || '', failed, reason, category, sdkApplicantId);
  };

  // Directly fetch data from Sumsub API via edge function with retry for address/POA
  const fetchExtractedDataFromSumsub = async (userEmail: string, failed = false, reason?: string, category?: 'biometrics' | 'document' | 'aml' | 'other', sdkApplicantId?: string) => {
    const maxRetries = 5;
    const retryDelays = [2000, 4000, 8000, 15000, 25000]; // Progressive delays - POA address extraction can take 30s+

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // Wait before calling — Sumsub needs time to finish processing documents/POA
        await new Promise(resolve => setTimeout(resolve, retryDelays[attempt]));
        
        // Only pass applicantId if it's a real Sumsub ID (not 'unknown' fallback)
        const realApplicantId = sdkApplicantId && sdkApplicantId !== 'unknown' ? sdkApplicantId : undefined;
        const { data, error } = await supabase.functions.invoke('fetch-sumsub-data', {
          body: { 
            email: userEmail, 
            forceRefresh: attempt > 0, 
            // Pass the applicantId directly from the Sumsub SDK — most reliable lookup
            ...(realApplicantId ? { applicantId: realApplicantId } : {}),
            // Pass the stable externalUserId (personId if set, else the session random ID)
            externalUserId: externalUserId || undefined,
            // Pass person's full name so edge function can do name-based lookup
            ...(personName ? { personName } : {}),
          }
        });

        if (error) {
          console.error('Error fetching Sumsub data:', error);
          if (attempt === maxRetries - 1) {
            await pollForExtractedData(userEmail, failed, reason, category);
            return;
          }
          continue;
        }
        // noNewVerification means we already have this data stored — use the returned extracted fields
        if (data?.noNewVerification && data?.extracted) {

          const extractedData: ExtractedData = {
            firstName: data.extracted.firstName,
            lastName: data.extracted.lastName,
            dateOfBirth: data.extracted.dateOfBirth,
            address: data.extracted.address,
            document: {
              documentType: data.extracted.documentType,
              documentNumber: data.extracted.documentNumber,
              documentExpiry: data.extracted.documentExpiry,
              documentIssueDate: data.extracted.documentIssueDate,
              documentCountry: data.extracted.documentCountry,
              nationality: data.extracted.nationality,
            },
            poa: (data.extracted.poaVerified || data.extracted.poaAddress) ? {
              address: data.extracted.poaAddress || data.extracted.address,
              documentType: data.extracted.poaDocumentType,
            } : undefined,
            verificationFailed: failed,
            failureReason: reason,
            failureCategory: category,
          };
          setFetchingData(false);
          onComplete(extractedData);
          return;
        }

        if (data?.extracted) {
          const hasAddress = data.extracted.address?.street || data.extracted.address?.city;
          
          // If we have personal data but no address yet, retry once more (not aggressively)
          if (data.extracted.firstName && !hasAddress && attempt < maxRetries - 1) {
            continue;
          }

          const extractedData: ExtractedData = {
            firstName: data.extracted.firstName,
            lastName: data.extracted.lastName,
            dateOfBirth: data.extracted.dateOfBirth,
            address: data.extracted.address,
            document: {
              documentType: data.extracted.documentType,
              documentNumber: data.extracted.documentNumber,
              documentExpiry: data.extracted.documentExpiry,
              documentIssueDate: data.extracted.documentIssueDate,
              documentCountry: data.extracted.documentCountry,
              nationality: data.extracted.nationality,
            },
            poa: (data.extracted.poaVerified || data.extracted.poaAddress) ? {
              address: data.extracted.poaAddress || data.extracted.address,
              documentType: data.extracted.poaDocumentType,
            } : undefined,
            // Add failure info if applicable
            verificationFailed: failed || data.extracted.verificationFailed,
            failureReason: reason || data.extracted.failureReason,
            failureCategory: category || data.extracted.failureCategory,
          };
          
          setFetchingData(false);
          onComplete(extractedData);
          return;
        }

        // If no extracted data and not last attempt, retry
        if (attempt < maxRetries - 1) {
          continue;
        }

        await pollForExtractedData(userEmail, failed, reason, category);
      } catch (err) {
        console.error('Error in fetchExtractedDataFromSumsub:', err);
        if (attempt === maxRetries - 1) {
          await pollForExtractedData(userEmail, failed, reason, category);
        }
      }
    }
  };

  // Determine the level name based on whether POA is included
  // If includePOA is true, use a level that includes POA (default: 'AI Onboarding')
  // If includePOA is false, use a simpler ID-only level if configured
  const effectiveLevelName = levelName || (includePOA ? 'AI Onboarding' : 'AI Onboarding');
  
  const { launch, destroy, isLoading, isReady, error, isDemo, externalUserId } = useSumsub({
    levelName: effectiveLevelName,
    // email/phone are used for backend session linking only, NOT passed to SDK init
    email,
    phone,
    // If a personId was passed (e.g. from the /verify link), use it as the stable
    // externalUserId so Sumsub ties the token to the correct stakeholder.
    userId: personId,
    onComplete: async (payload) => {
      
      // Check if verification was rejected (RED) due to failed biometrics or AML
      if (payload.reviewResult?.reviewAnswer === 'RED') {
        const rejectLabels = payload.reviewResult.rejectLabels || [];
        const reviewRejectType = payload.reviewResult.reviewRejectType;
        
        // Check for biometric failures
        const hasLivenessFailure = rejectLabels.some((l: string) => 
          l.includes('LIVENESS') || l.includes('SPOOFING') || l.includes('ANTI_FRAUD')
        );
        const hasFaceMatchFailure = rejectLabels.some((l: string) => 
          l.includes('FACE') || l.includes('COMPARISON') || l.includes('SELFIE_MISMATCH')
        );
        
        // Check for AML/sanctions failures
        const hasSanctionsMatch = rejectLabels.some((l: string) => 
          l.includes('SANCTIONS') || l.includes('WATCHLIST') || l.includes('BLACKLIST')
        );
        const hasPEPMatch = rejectLabels.some((l: string) => 
          l.includes('PEP') || l.includes('POLITICALLY')
        );
        const hasAdverseMedia = rejectLabels.some((l: string) => 
          l.includes('ADVERSE') || l.includes('MEDIA') || l.includes('NEWS')
        );
        
        // Check for document failures
        const hasDocumentFailure = rejectLabels.some((l: string) => 
          l.includes('DOCUMENT') || l.includes('FORGERY') || l.includes('TAMPERED')
        );
        
        // Determine failure category and reason
        let failureCategory: 'biometrics' | 'document' | 'aml' | 'other' = 'other';
        let failureReason = 'Verification could not be completed.';
        
        if (hasSanctionsMatch) {
          failureCategory = 'aml';
          failureReason = 'AML screening match detected. Our compliance team will review your application.';
        } else if (hasLivenessFailure && hasFaceMatchFailure) {
          failureCategory = 'biometrics';
          failureReason = 'Liveness and face match verification failed. A new verification link may be sent to you.';
        } else if (hasLivenessFailure) {
          failureCategory = 'biometrics';
          failureReason = 'Liveness check failed. Please ensure good lighting and follow on-screen instructions.';
        } else if (hasFaceMatchFailure) {
          failureCategory = 'biometrics';
          failureReason = 'Face did not match your ID document photo.';
        } else if (hasDocumentFailure) {
          failureCategory = 'document';
          failureReason = 'Document verification failed. Please ensure you are using a valid, unexpired document.';
        } else if (hasPEPMatch || hasAdverseMedia) {
          failureCategory = 'aml';
          failureReason = 'Additional review required. Our compliance team will be in touch.';
        } else {
          failureReason = `Verification issue: ${rejectLabels.join(', ') || reviewRejectType || 'Unknown'}`;
        }
        
        // Allow user to continue but mark as failed - save failure details to backend
        await handleVerificationComplete(true, failureReason, failureCategory, payload.applicantId);
        return;
      }
      
      await handleVerificationComplete(false, undefined, undefined, payload.applicantId);
    },
    onError: (err) => {
      console.error('Sumsub verification error:', err);
      onError?.(err.message);
    },
    onStepCompleted: (step, payload) => {
      // Show manual continue after POA step or final step
      if (step === 'PROOF_OF_RESIDENCE' || step === 'APPLICANT_DATA' || step === 'SELFIE') {
        setTimeout(() => {
          if (!completionHandled.current) {
            setShowManualContinue(true);
          }
        }, 3000);
      }
    }
  });

  // Poll for extracted data from the session
  const pollForExtractedData = async (userEmail: string, failed = false, reason?: string, category?: 'biometrics' | 'document' | 'aml' | 'other') => {
    const maxAttempts = 5;
    const delayMs = 1500;
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // Wait before polling (webhook needs time to process)
        await new Promise(resolve => setTimeout(resolve, delayMs));
        
        // Use edge function to query session data (RLS-safe)
        const { data: lookupResult, error: fetchError } = await supabase.functions.invoke('onboarding-session-lookup', {
          body: {
            action: 'get_session_data',
            email: userEmail,
          }
        });
        
        if (fetchError) {
          continue;
        }
        
        const sessionData = lookupResult?.onboarding_data as Record<string, unknown> | null;
        
        // Check if webhook has updated the session with extracted data
        if (sessionData?.extractedFirstName || sessionData?.extractedDocumentNumber) {
          
          const extractedData: ExtractedData = {
            firstName: sessionData.extractedFirstName as string,
            lastName: sessionData.extractedLastName as string,
            dateOfBirth: sessionData.extractedDateOfBirth as string,
            address: sessionData.extractedAddress as ExtractedAddress,
            document: {
              documentNumber: sessionData.extractedDocumentNumber as string,
              documentExpiry: sessionData.extractedDocumentExpiry as string,
              documentIssueDate: sessionData.extractedDocumentIssueDate as string,
              documentCountry: sessionData.extractedDocumentCountry as string,
              nationality: sessionData.extractedNationality as string,
            },
            poa: sessionData.poaVerified ? {
              address: sessionData.poaExtractedAddress as ExtractedAddress,
              documentType: sessionData.poaDocumentType as string,
              issueDate: sessionData.poaIssueDate as string,
              issuer: sessionData.poaIssuer as string,
            } : undefined,
            // Include failure info
            verificationFailed: failed,
            failureReason: reason,
            failureCategory: category,
          };
          
          setFetchingData(false);
          onComplete(extractedData);
          return;
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }
    
    // Max attempts reached with no extracted data.
    // If failed (RED) or no data found — just proceed with whatever we have.
    // NEVER re-launch the SDK here: that creates an infinite loop of token requests.
    setFetchingData(false);
    if (failed) {
      onComplete({ verificationFailed: true, failureReason: reason, failureCategory: category });
    } else {
      onComplete({});
    }
  };

  // Launch SDK on mount with a small delay to ensure DOM is ready
  useEffect(() => {
    if (!hasLaunched.current) {
      hasLaunched.current = true;
      
      // Use requestAnimationFrame to ensure DOM is painted
      const frameId = requestAnimationFrame(() => {
        // Double-check element exists before launching
        const container = document.getElementById('sumsub-websdk-container');
        if (container) {
          launch('#sumsub-websdk-container');
        } else {
          console.error('Sumsub container not found in DOM');
          hasLaunched.current = false;
        }
      });
      
      return () => {
        cancelAnimationFrame(frameId);
        destroy();
      };
    }
    
    return () => {
      destroy();
    };
  }, [launch, destroy]);

  if (error) {
    return (
      <div className="border-2 border-destructive/30 rounded-xl p-6 text-center bg-destructive/5">
        <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-3" />
        <p className="font-medium text-foreground mb-1">Verification Error</p>
        <p className="text-sm text-muted-foreground mb-4">{error}</p>
        <button
          onClick={() => {
            hasLaunched.current = false;
            launch('#sumsub-websdk-container');
          }}
          className="px-4 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-medium hover:bg-accent/90"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (verificationComplete) {
    // Show different UI based on whether verification failed
    if (verificationFailed) {
      return (
        <div className="border-2 border-yellow-500 rounded-xl p-6 text-center bg-yellow-50 dark:bg-yellow-950/20 animate-fade-in-up">
          {fetchingData ? (
            <>
              <Loader2 className="w-12 h-12 text-accent mx-auto mb-3 animate-spin" />
              <p className="font-medium text-foreground mb-1">Processing...</p>
              <p className="text-sm text-muted-foreground">
                Saving your information
              </p>
            </>
          ) : (
            <>
              <AlertTriangle className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
              <p className="font-medium text-foreground mb-1">Verification Requires Review</p>
              <p className="text-sm text-muted-foreground mb-4">
                {failureReason}
              </p>
              <div className="p-3 bg-background rounded-lg border border-border">
                <p className="text-xs text-muted-foreground">
                  You can continue with your application. Our support team will be in touch if a new verification link is required.
                </p>
              </div>
            </>
          )}
        </div>
      );
    }
    
    return (
      <div className="border-2 border-success rounded-xl p-6 text-center bg-success/5 animate-fade-in-up">
        {fetchingData ? (
          <>
            <Loader2 className="w-12 h-12 text-accent mx-auto mb-3 animate-spin" />
            <p className="font-medium text-foreground mb-1">Processing verification...</p>
            <p className="text-sm text-muted-foreground">
              Extracting your information from verified documents
            </p>
          </>
        ) : (
          <>
            <CheckCircle2 className="w-12 h-12 text-success mx-auto mb-3" />
            <p className="font-medium text-foreground mb-1">Verification Complete!</p>
            <p className="text-sm text-muted-foreground">
              {includePOA 
                ? 'Your identity and address have been verified successfully.'
                : 'Your identity has been verified successfully.'
              }
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full max-w-md mx-auto">
      {/* Loading state */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-12 h-12 text-accent mb-3 animate-spin" />
          <p className="font-medium text-foreground mb-1">Initializing verification...</p>
          <p className="text-sm text-muted-foreground text-center">
            Please wait while we set up secure verification
          </p>
        </div>
      )}
      
      {/* Sumsub SDK Container - constrained to parent width
          MUST remain empty (no React children) - SDK manipulates DOM directly */}
      <div 
        ref={containerRef}
        id="sumsub-websdk-container" 
        className="w-full rounded-2xl overflow-hidden [&_iframe]{max-width:100%!important}"
        style={{ 
          minHeight: isLoading ? '0' : '500px',
          display: isLoading ? 'none' : 'block',
          maxWidth: '100%',
        }}
      />

      {/* Manual continue button - shows if SDK completion event doesn't fire */}
      {showManualContinue && !verificationComplete && (
        <div className="mt-6 pt-4 border-t border-border">
          <p className="text-sm text-muted-foreground text-center mb-3">
            Completed all verification steps? Click below to continue.
          </p>
          <button
            onClick={() => handleVerificationComplete(false)}
            className="w-full h-14 bg-accent text-accent-foreground font-semibold rounded-full hover:bg-accent/90 transition-colors"
          >
            I've Completed Verification
          </button>
        </div>
      )}
    </div>
  );
}
